const listeners = new Set();

const AXIOS_FINISH_KEY = "__elsaGlobalLoadingFinish";

let activeRequestCount = 0;

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

export const getGlobalLoadingSnapshot = () => activeRequestCount;

export const subscribeGlobalLoading = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const shouldTrackGlobalLoading = (options = {}) =>
  options?.skipGlobalLoading !== true && options?.globalLoading !== false;

export const beginGlobalLoading = () => {
  let finished = false;

  activeRequestCount += 1;
  notifyListeners();

  return () => {
    if (finished) return;
    finished = true;

    activeRequestCount = Math.max(0, activeRequestCount - 1);
    notifyListeners();
  };
};

export const runWithGlobalLoading = (operation, options = {}) => {
  if (!shouldTrackGlobalLoading(options)) {
    return operation();
  }

  const finish = beginGlobalLoading();

  try {
    return Promise.resolve(operation()).then(
      (result) => {
        finish();
        return result;
      },
      (error) => {
        finish();
        throw error;
      }
    );
  } catch (error) {
    finish();
    throw error;
  }
};

export const startAxiosGlobalLoading = (config) => {
  if (!config || !shouldTrackGlobalLoading(config) || config[AXIOS_FINISH_KEY]) {
    return config;
  }

  config[AXIOS_FINISH_KEY] = beginGlobalLoading();
  return config;
};

export const finishAxiosGlobalLoading = (config) => {
  const finish = config?.[AXIOS_FINISH_KEY];
  if (typeof finish !== "function") return;

  delete config[AXIOS_FINISH_KEY];
  finish();
};

export const installAxiosGlobalLoading = (axiosInstance) => {
  const requestInterceptor = axiosInstance.interceptors.request.use(
    (config) => startAxiosGlobalLoading(config),
    (error) => {
      finishAxiosGlobalLoading(error?.config);
      return Promise.reject(error);
    }
  );

  const responseInterceptor = axiosInstance.interceptors.response.use(
    (response) => {
      finishAxiosGlobalLoading(response?.config);
      return response;
    },
    (error) => {
      finishAxiosGlobalLoading(error?.config);
      return Promise.reject(error);
    }
  );

  return () => {
    axiosInstance.interceptors.request.eject(requestInterceptor);
    axiosInstance.interceptors.response.eject(responseInterceptor);
  };
};

// Réservé aux tests unitaires du store afin d'isoler chaque scénario.
export const resetGlobalLoadingForTests = () => {
  activeRequestCount = 0;
  notifyListeners();
};
